import { CSSProperties, useMemo } from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '../../lib/cn';

/**
 * Skeleton — placeholder con shimmer suave (DESIGN-SYSTEM §3.20).
 *
 * - Variante por defecto: caja unica con `width`/`height` opcionales.
 * - Variante texto: `lines > 1` renderiza varias lineas con la ultima al 70%
 *   de ancho para sensacion natural.
 * - Animacion: gradient horizontal (`background-position`) sobre el token
 *   `cream-200`. Con `prefers-reduced-motion`, se conserva el color de base
 *   sin animar (DESIGN-SYSTEM §3.20).
 *
 * El shimmer no requiere keyframes en CSS porque usamos inline animation con
 * `background-image` lineal y `animationName` registrado via style tag.
 */
export type SkeletonRounded = 'none' | 'sm' | 'md' | 'lg' | 'pill';

export interface SkeletonProps {
  /** Ancho CSS (number => px, string => valor literal). */
  width?: number | string;
  /** Alto CSS. */
  height?: number | string;
  /** Radio. Default `sm`. */
  rounded?: SkeletonRounded;
  /** Numero de lineas (variante texto). Si > 1, se ignora `height` y se usa altura tipo body. */
  lines?: number;
  /** Aria-label opcional para regiones complejas. */
  'aria-label'?: string;
  className?: string;
}

const roundedClasses: Record<SkeletonRounded, string> = {
  none: '',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  pill: 'rounded-pill',
};

// Animacion inline para no depender de keyframes en index.css. El gradiente
// se mueve via `background-position`. Usamos CSS animation declarativa.
const SHIMMER_STYLE: CSSProperties = {
  // Gradient claro -> tinte mas claro -> claro. Usamos tokens en CSS vars.
  backgroundImage:
    'linear-gradient(90deg, var(--color-cream-200) 0%, var(--color-cream-100) 50%, var(--color-cream-200) 100%)',
  backgroundSize: '200% 100%',
  animation: 'ceci-skeleton-shimmer 1.4s ease-in-out infinite',
};

const STATIC_STYLE: CSSProperties = {
  backgroundColor: 'var(--color-cream-200)',
};

// Inyectamos keyframes una sola vez por modulo (idempotente).
let keyframesInjected = false;
function ensureKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return;
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-ceci-skeleton', 'true');
  styleEl.textContent = `@keyframes ceci-skeleton-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }`;
  document.head.appendChild(styleEl);
  keyframesInjected = true;
}

function toCssSize(value: number | string | undefined): string | undefined {
  if (value == null) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

export function Skeleton({
  width,
  height,
  rounded = 'sm',
  lines = 1,
  className,
  'aria-label': ariaLabel,
}: SkeletonProps) {
  const reduceMotion = useReducedMotion();
  ensureKeyframes();

  const baseStyle = reduceMotion ? STATIC_STYLE : SHIMMER_STYLE;

  const blocks = useMemo(() => {
    const count = Math.max(1, Math.floor(lines));
    return Array.from({ length: count });
  }, [lines]);

  // Variante texto (>=2 lineas): usamos altura tipo body, ultima al 70%.
  if (lines > 1) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label={ariaLabel ?? 'Cargando contenido'}
        className={cn('flex flex-col gap-2', className)}
      >
        {blocks.map((_, idx) => {
          const isLast = idx === blocks.length - 1;
          const style: CSSProperties = {
            ...baseStyle,
            height: toCssSize(height) ?? '1em',
            width: isLast ? '70%' : (toCssSize(width) ?? '100%'),
          };
          return (
            <span
              key={idx}
              aria-hidden="true"
              className={cn('block', roundedClasses[rounded])}
              style={style}
            />
          );
        })}
        <span className="sr-only">Cargando</span>
      </div>
    );
  }

  const style: CSSProperties = {
    ...baseStyle,
    width: toCssSize(width) ?? '100%',
    height: toCssSize(height) ?? '1em',
  };

  return (
    <span
      role="status"
      aria-busy="true"
      aria-label={ariaLabel ?? 'Cargando'}
      className={cn('block', roundedClasses[rounded], className)}
      style={style}
    >
      <span className="sr-only">Cargando</span>
    </span>
  );
}
